import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { useLanguage } from '../../contexts/LanguageContext';

const CheckoutOverlay: React.FC = () => {
    const { isCheckoutOpen, closeCheckout, cartItems, cartTotal } = useCart();
    const { lang } = useLanguage();
    const [step, setStep] = useState(1); // 1: Info, 2: Redirecting/Processing, 3: Success
    const [email, setEmail] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handlePayment = async () => {
        if (!email || !email.includes('@')) {
            setError(lang === 'es' ? 'Por favor introduce un email válido' : 'Please enter a valid email');
            return;
        }

        setIsProcessing(true);
        setError(null);

        try {
            // Updated endpoint to match api/create-checkout.ts
            const response = await fetch('/api/create-checkout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: cartItems,
                    email: email
                }),
            });

            const data = await response.json() as { url?: string; error?: string };

            if (data.url) {
                // Redirect to Stripe Hosted Checkout
                window.location.href = data.url;
            } else {
                throw new Error(data.error || 'Failed to create session');
            }
        } catch (err: any) {
            console.error('Checkout error:', err);
            setError(err.message || 'Checkout error. Please ensure Stripe is configured.');
            setIsProcessing(false);
        }
    };

    if (!isCheckoutOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] bg-black">
            <div className="absolute inset-0 bg-gradient-to-b from-[#1c1917] to-black opacity-90" />

            <div className="relative z-10 h-full max-w-7xl mx-auto px-4 py-8 md:py-12 flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-3xl font-heading font-bold text-[#fef3c7] tracking-widest">CHECKOUT</h2>
                    <button
                        onClick={closeCheckout}
                        className="text-white/50 hover:text-white flex items-center gap-2 transition-colors"
                    >
                        <span className="hidden md:inline text-xs uppercase tracking-widest">Back to Gallery</span>
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex-1 grid md:grid-cols-2 gap-12 overflow-hidden">
                    {/* Left: Summary */}
                    <div className="overflow-y-auto pr-4 custom-scrollbar">
                        <h3 className="text-sm uppercase text-[#fbbf24] tracking-widest mb-6 border-b border-[#fbbf24]/20 pb-2">Order Summary</h3>

                        {cartItems.length === 0 ? (
                            <div className="text-white/30 text-center py-12">Your cart is empty.</div>
                        ) : (
                            <div className="space-y-6">
                                {cartItems.map((item, idx) => (
                                    <div key={`${item.id}-${idx}`} className="flex gap-4">
                                        <div className="w-20 h-20 bg-[#292524] rounded-md overflow-hidden flex-shrink-0 flex items-center justify-center border border-white/10">
                                            {item.image ? (
                                                <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                                            ) : (
                                                <span className="text-white/20 text-[8px] uppercase font-bold text-center px-2">{item.title}</span>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="font-bold text-white text-lg">{item.title}</h4>
                                            <p className="text-white/50 text-xs uppercase tracking-wider mb-1">{item.type}</p>
                                            <div className="font-mono text-[#fbbf24]">{item.price === 0 ? 'Inquire' : `${(item.price / 100).toFixed(0)}€`}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="mt-8 border-t border-white/10 pt-6">
                            <div className="flex justify-between items-end">
                                <span className="text-white/50 text-sm uppercase">Total</span>
                                <span className="text-3xl font-mono text-[#fef3c7]">{(cartTotal / 100).toFixed(0)}€</span>
                            </div>
                        </div>
                    </div>

                    {/* Right: Action */}
                    <div className="bg-[#1c1917] p-8 rounded-2xl border border-white/5 h-fit shadow-2xl">
                        <AnimatePresence mode="wait">
                            {step === 1 && (
                                <motion.div
                                    key="step1"
                                    initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                                >
                                    <h3 className="text-xl font-bold text-white mb-6">Customer Information</h3>
                                    <div className="space-y-4 mb-6">
                                        <div className="space-y-1">
                                            <label className="text-[10px] uppercase text-white/40 tracking-widest ml-1">Email</label>
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="tu@email.com"
                                                className="w-full bg-black/50 border border-white/10 p-4 rounded-lg text-white focus:outline-none focus:border-[#fbbf24] transition-colors"
                                            />
                                        </div>
                                    </div>

                                    {error && (
                                        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg">
                                            {error}
                                        </div>
                                    )}

                                    <button
                                        onClick={handlePayment}
                                        disabled={isProcessing || cartItems.length === 0}
                                        className="w-full bg-[#fbbf24] text-black py-4 font-bold uppercase tracking-widest hover:bg-[#f59e0b] transition-all flex justify-center items-center gap-2 disabled:opacity-50 shadow-lg shadow-[#fbbf24]/20"
                                    >
                                        {isProcessing ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                <span>{lang === 'es' ? 'PROCESANDO...' : 'PROCESSING...'}</span>
                                            </>
                                        ) : (
                                            <>
                                                <span>{lang === 'es' ? 'CONTINUAR AL PAGO' : 'PROCEED TO PAYMENT'}</span>
                                                <ArrowRight className="w-4 h-4" />
                                            </>
                                        )}
                                    </button>

                                    <p className="mt-4 text-[10px] text-white/30 text-center uppercase tracking-widest">
                                        Secure payment via Stripe
                                    </p>
                                </motion.div>
                            )}

                            {step === 3 && (
                                <motion.div
                                    key="step3"
                                    initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                                    className="text-center py-12"
                                >
                                    <div className="w-20 h-20 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <CheckCircle className="w-10 h-10" />
                                    </div>
                                    <h3 className="text-3xl font-bold text-white mb-2">Order Confirmed!</h3>
                                    <p className="text-white/50 mb-8">Thank you for your purchase. A confirmation email has been sent.</p>
                                    <button
                                        onClick={closeCheckout}
                                        className="px-8 py-3 border border-[#fbbf24] text-[#fbbf24] uppercase tracking-widest hover:bg-[#fbbf24] hover:text-black transition-colors"
                                    >
                                        Return to Gallery
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutOverlay;
